type MembersMarketData = {
	use_custom_primary_dates?: boolean | null
	default_start_day?: number | null
	default_end_day?: number | null
	start_date: string
	end_date: string
	use_extra_open_window?: boolean | null
	extra_open_start_date?: string | null
	extra_open_end_date?: string | null
}

function computePrimaryWindow(data: MembersMarketData): { start: Date; end: Date } {
	if (data.use_custom_primary_dates) {
		return { start: new Date(data.start_date), end: new Date(data.end_date) }
	}
	const now = new Date()
	let y = now.getUTCFullYear()
	let m = now.getUTCMonth()
	const startDay = data.default_start_day ?? 1
	const endDay = data.default_end_day ?? 5
	// If this month's window has already closed, advance to next month
	if (now > new Date(Date.UTC(y, m, endDay, 23, 59, 59))) {
		m += 1
		if (m > 11) { m = 0; y += 1 }
	}
	return {
		start: new Date(Date.UTC(y, m, startDay, 0, 0, 0)),
		end: new Date(Date.UTC(y, m, endDay, 23, 59, 59))
	}
}

/**
 * Determines whether the Members Market is currently open.
 * When use_custom_primary_dates is falsy, open/close dates are computed from
 * default_start_day / default_end_day (falling back to 1st–5th of current month).
 * An optional extra open window is supported via use_extra_open_window + extra dates.
 */
export default function isMembersMarketOpen(
	data: MembersMarketData,
	forceMarketOpen?: string
) {
	const { start, end } = computePrimaryWindow(data)
	const now = new Date()
	const isForcedOpen = forceMarketOpen === 'true'

	const isInPrimary = now >= start && now <= end
	const isInExtra =
		data.use_extra_open_window &&
		data.extra_open_start_date &&
		data.extra_open_end_date
			? now >= new Date(data.extra_open_start_date) &&
			  now <= new Date(data.extra_open_end_date)
			: false

	return {
		isCurrentlyOpen: isForcedOpen || isInPrimary || isInExtra,
		isBeforeOpenDate: now < start,
		isBeforeCloseDate: now < end,
		computedStartDate: start.toISOString(),
		computedEndDate:
			isInExtra && !isInPrimary && data.extra_open_end_date
				? new Date(data.extra_open_end_date).toISOString()
				: end.toISOString()
	}
}
